import { useState, useCallback, useRef, useEffect } from "react";
import {
  displayInfoToast,
  displayErrorToast,
  displaySucessToast,
} from "@endya-conformite/ui-kit";
import { downloadFile } from "@src/helper/file.helper";
import { FilesApi } from "../files.api";

const MAXIMUM_RETRY = 6;
const RETRY_INTERVAL = 30_000 / MAXIMUM_RETRY;

type UseDossierZipExportParams = {
  controleQuinquennalId: string | undefined;
  email?: string;
  onExported?: () => void;
};

/**
 * Export zip de toutes les pièces d'un dossier (tous axes confondus).
 * Pendant du `useZipExport` mono-axe, avec le même flux : déclenchement
 * fire-and-forget côté gateway puis polling de l'URL signée.
 */
export const useDossierZipExport = ({
  controleQuinquennalId,
  email,
  onExported,
}: UseDossierZipExportParams) => {
  const [isExporting, setIsExporting] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const isMountedRef = useRef(true);
  const onExportedRef = useRef(onExported);
  onExportedRef.current = onExported;

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const retryAttempt = useCallback(async (idCq: string, attempt = 0) => {
    if (!isMountedRef.current) return;
    if (attempt >= MAXIMUM_RETRY) {
      displayErrorToast(
        "Le traitement est plus long que prévu, nous vous enverrons un mail dès que les documents seront prêts."
      );
      setIsExporting(false);
      return;
    }

    const url = await FilesApi.getDossierZipSignedUrl(idCq);

    if (!isMountedRef.current) return;

    if (url) {
      displaySucessToast("L'export est prêt, vous pouvez le télécharger");
      downloadFile(url);
      setIsExporting(false);
      onExportedRef.current?.();
      return;
    }

    timeoutRef.current = setTimeout(() => {
      void retryAttempt(idCq, attempt + 1);
    }, RETRY_INTERVAL);
  }, []);

  const triggerExport = useCallback(async () => {
    if (!controleQuinquennalId) return;
    if (isExporting) return;

    setIsExporting(true);

    displayInfoToast(
      "Les documents sont en cours de préparation. Merci de patienter.",
      { hideAfter: 10_000 }
    );

    try {
      await FilesApi.triggerDossierZipExport(controleQuinquennalId, email);
      void retryAttempt(controleQuinquennalId);
    } catch {
      displayErrorToast(
        "Une erreur est survenue, merci de réessayer plus tard.",
        { hideAfter: 10_000 }
      );
      setIsExporting(false);
    }
  }, [controleQuinquennalId, email, isExporting, retryAttempt]);

  return {
    triggerExport,
    isExporting,
  };
};
